import {
	fireEvent,
	render,
	screen,
	waitFor,
	within,
} from "@testing-library/react";
import Immutable from "immutable";
import { http, HttpResponse } from "msw";
import { generateStorageViewKey } from "../../common/useLocalSearchView";
import { trim } from "../../search/utils";

export const paging = {
	page: 0,
	pageSize: 50,
	pages: 241,
	results: 12026,
	resultsUnfiltered: 12026,
};

export const getResponse = (data, status = 200) => {
	const response = Object.assign({}, { paging }, data);
	return HttpResponse.json(response, { status });
};

export const defaultHandler = (path, response = []) =>
	http.get(path, () => HttpResponse.json(response));

export const defaultPostHandler = (path) =>
	http.post(path, () => new HttpResponse(null, { status: 204 }));

export const defaultDeleteHandler = (path) =>
	http.delete(path, () => new HttpResponse(null, { status: 204 }));

export async function locationIsChanged(previousURL, newURL) {
	window.location = new URL(newURL);
	window.dispatchEvent(
		new HashChangeEvent("hashchange", {
			oldURL: previousURL,
			newURL,
		}),
	);
}

export async function locationIsChangedReloadingResults(previousURL, newURL) {
	window.location = new URL(newURL);
	window.dispatchEvent(
		new HashChangeEvent("hashchange", {
			oldURL: previousURL,
			newURL,
		}),
	);
	await waitFor(() => screen.getByText("Loading results..."));
}

export async function loadSearchResults(url, searchComponent) {
	window.location = new URL(url);
	render(searchComponent);
	await waitFor(() => screen.getAllByRole("table"));

	return url;
}

export function openAdvancedSearchModal(isMobile = false) {
	if (isMobile) {
		const header =
			screen.getByText("Quick Search").parentElement.parentElement;
		fireEvent.click(within(header).getByText("More"));
		fireEvent.click(screen.getByText("Advanced Search"));
	} else {
		const buttons =
			screen.getByText("Quick Search").parentElement.previousSibling;
		fireEvent.click(within(buttons).getByText("Advanced Search"));
	}

	return screen.getByRole("dialog");
}

export function hasText(selector, text) {
	return selector.getByText((content, node) => {
		const hasText = (node) => node.textContent === text;
		const nodeHasText = hasText(node);
		const childrenDontHaveText = Array.from(node.children).every(
			(child) => !hasText(child),
		);

		return nodeHasText && childrenDontHaveText;
	});
}

export const randomString = (length = 8) => {
	const characters =
		"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;

	let result = "";
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength),
		);
	}
	return result;
};

export const randomNumericString = (length = 8) => {
	const characters = "123456789";
	const charactersLength = characters.length;

	let result = "";
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength),
		);
	}
	return result;
};

export const randomNumber = (length = 2) => {
	return Math.floor(
		Math.pow(10, length - 1) +
			Math.random() *
				(Math.pow(10, length) - Math.pow(10, length - 1) - 1),
	);
};

export const randomArrayOfString = (arrayLength, stringLength = 8) => {
	return [...Array(arrayLength)].map(() => {
		return randomString(stringLength);
	});
};

export function clearLocalStorage() {
	window.localStorage.clear();
}

export function setLocalView(view, namespace) {
	window.localStorage.setItem(
		generateStorageViewKey(namespace),
		JSON.stringify(view),
	);
}

export function getTrimmedSearchName(searchName) {
	return trim(searchName, 15);
}

export function getTrimmedViewName(viewName) {
	return trim(viewName, 20);
}

export async function checkSearchAndViewName(searchName, viewName) {
	const searchNameContainer =
		screen.getByText("Current search").parentElement;
	await waitFor(() =>
		expect(
			within(searchNameContainer).getByText(
				getTrimmedSearchName(searchName),
			),
		).toBeDefined(),
	);

	const viewNameContainer = await waitFor(
		() =>
			screen.getByText("Edit View").parentElement.parentElement
				.previousSibling.childNodes[1].childNodes[0],
	);
	await waitFor(() =>
		expect(
			within(viewNameContainer).getByText(getTrimmedViewName(viewName)),
		).toBeDefined(),
	);

	return true;
}

export const getChoices = (choiceNumber = 3) => {
	return [...Array(choiceNumber)].map(() => ({
		key: randomString(),
		value: randomString(),
	}));
};

export const getSelectedChoices = (key, label, choiceNumber = 1) => {
	return Immutable.Map({
		[key]: Immutable.Map({ label, choices: getChoices(choiceNumber) }),
	});
};

export const getParams = (request) => new URL(request.url).searchParams;
